import { BrowserRouter } from 'react-router-dom';
import { ILocalizer } from '@agilepoint/wat-react';
import { RestateProvider, ScopeRegistrar } from '@agilepoint/react-restate';
import { IAppContext, IAuthorizationContext, IConfigurationBase } from '@agilepoint/wat';
import {
    WatReactAppContainer, PopupMessageHost, WindowSizeProvider, WithNotificationList, WithPopup, IIconSprite
} from '@agilepoint/wat-react-ux';
import { memo, Component } from 'react';
import { Store } from 'redux';
import { IIocContainerBuilder } from '@agilepoint/restate-core/lib/ioc-container';
import { combineUrl, memoizeFunction } from '@agilepoint/utils-common';
import styled from 'styled-components';
import { PermissionCode } from './lib/authorization';
import { CoreAppLayoutContainer } from './modules/core/containers';
import { createConfigScopeBuilder } from './modules/core/scopes';
import { appContextDependency } from './dependencies';
import { IApplicationConfig } from './types';
import { LoadingIllustration, ServiceClientProvider } from './common';
import { QueryClient, QueryClientProvider } from 'react-query';
import { MenuItemsProvider } from 'modules/core/providers/provider';
import modules from 'modules';
import '@agilepoint/icons-mobile-app-accelerator';
import { ErrorBoundary } from 'react-error-boundary';
import { ErrorFallback } from 'common/components/api-error/error-fallback';

export interface IAppProps<TConfig extends IConfigurationBase> {
    appContext: IAppContext<TConfig>;
    authorizationContext: IAuthorizationContext<PermissionCode>;
    localizer: ILocalizer;
    store: Store;
    routes: React.ReactNode[];
}

const BottomRightNotificationList = styled(WithNotificationList)`
    bottom: 50px;
    right: 32px;
    top: unset;
    left: unset;
    width: auto;
`;

interface IAppRouterProps {
    appContext: IAppContext<IApplicationConfig>;
    children: any;
}

const AppRouter = memo(({ appContext, children }: IAppRouterProps) => (
    <BrowserRouter basename={appContext.configuration.baseUrl}>
        {children}
    </BrowserRouter>
));

function getIconsPath(basePath: string) {
    const genericIconsSpritePath = combineUrl(basePath, '/assets/icons/generic/sprite.symbol.svg');
    const mobileAppAcceleratorIconsSpritePath = combineUrl(basePath, '/assets/icons/mobile-app-accelerator/sprite.symbol.svg');
    const iconSpriteAddons: IIconSprite[] = [
        { name: 'workcenter', src: mobileAppAcceleratorIconsSpritePath }
    ];
    return { genericIconsSpritePath, iconSpriteAddons };
}

// Create a client
const queryClient = new QueryClient({ defaultOptions: { queries: { refetchOnWindowFocus: false, cacheTime: 0 } } });

export class App<TConfig extends IApplicationConfig> extends Component<IAppProps<TConfig>>{
    private configScopeFactory: any;

    private loaderFallback = memoizeFunction((error?: Error) => (
        error
            ? <span>{error.message}</span>
            : <LoadingIllustration />
    ));

    constructor(props: IAppProps<TConfig>) {
        super(props);
        const { appContext: { configuration } } = props;
        this.configScopeFactory = createConfigScopeBuilder(configuration);
    }

    render() {
        const { store, routes, appContext, localizer } = this.props;
        const { genericIconsSpritePath, iconSpriteAddons } = getIconsPath(appContext.configuration.basePath);

        return (
            <RestateProvider store={store} onBuildIocContainer={this.onBuildIocContainer} >
                <QueryClientProvider client={queryClient}>
                    <ScopeRegistrar scopeName="config" scopeCreator={this.configScopeFactory} fallback={this.loaderFallback}>
                        <WatReactAppContainer themeName="mobile-app-accelerator-default" iconSpriteAddons={iconSpriteAddons} genericIconSpritePath={genericIconsSpritePath} translate={localizer.translate} >
                            <ServiceClientProvider>
                                <BottomRightNotificationList>
                                    <WithPopup>
                                        <PopupMessageHost />
                                        <AppRouter appContext={appContext}>
                                            <WindowSizeProvider>
                                                <MenuItemsProvider modules={modules}>
                                                    <ErrorBoundary
                                                        FallbackComponent={ErrorFallback}>
                                                        <CoreAppLayoutContainer routes={routes} />
                                                    </ErrorBoundary>
                                                </MenuItemsProvider>
                                            </WindowSizeProvider>
                                        </AppRouter>
                                    </WithPopup>
                                </BottomRightNotificationList>
                            </ServiceClientProvider>
                        </WatReactAppContainer>
                    </ScopeRegistrar>
                </QueryClientProvider>
            </RestateProvider>
        );
    }

    private onBuildIocContainer = (builder: IIocContainerBuilder) => {
        appContextDependency.inject(builder, this.props.appContext);
    };
}
